import { COMMUNICATION_CATEGORIES } from '@/types';
import { getClassifierApiUrl } from '@/lib/runtimeConfig';
function normalizeAttachmentInsights(input) {
    if (!Array.isArray(input))
        return [];
    return input.map((item) => {
        const row = (item || {});
        return {
            name: String(row.name || 'attachment'),
            documentType: row.documentType != null ? String(row.documentType) : undefined,
            contentType: row.contentType != null ? String(row.contentType) : undefined,
            size: typeof row.size === 'number' ? row.size : undefined,
            kind: row.kind != null ? String(row.kind) : undefined,
            status: String(row.status || 'failed'),
            summary: String(row.summary || ''),
            keyFacts: Array.isArray(row.keyFacts)
                ? row.keyFacts.map(String)
                : [],
            entities: Array.isArray(row.entities)
                ? row.entities.map(String)
                : [],
        };
    });
}
function isCategory(value) {
    return COMMUNICATION_CATEGORIES.includes(value);
}
function normalizeCategories(input) {
    const list = Array.isArray(input) ? input.map(String) : [];
    const out = [];
    for (const item of list) {
        if (isCategory(item) && !out.includes(item))
            out.push(item);
    }
    return out.length > 0 ? out : ['General'];
}
function priorityToUrgency(priority) {
    const p = String(priority || 'medium').toLowerCase();
    if (p === 'critical')
        return { urgencyStatus: 'critical', urgencyScore: 95 };
    if (p === 'high')
        return { urgencyStatus: 'high', urgencyScore: 75 };
    if (p === 'low')
        return { urgencyStatus: 'low', urgencyScore: 20 };
    return { urgencyStatus: 'medium', urgencyScore: 50 };
}
export function mergeClassificationOntoBase(base, result) {
    const categories = normalizeCategories(result.categories);
    const { urgencyStatus, urgencyScore } = priorityToUrgency(result.priority);
    const confidence = typeof result.confidence === 'number' ? result.confidence : 0.5;
    const sentiment = (['negative', 'neutral', 'positive'].includes(String(result.sentiment || ''))
        ? result.sentiment
        : 'neutral');
    return {
        ...base,
        conversationId: result.conversationId || base.conversationId,
        internetMessageId: result.internetMessageId || base.internetMessageId,
        subject: result.subject || base.subject,
        category: categories[0],
        categories,
        urgencyScore,
        urgencyStatus,
        fraudScore: 0,
        fraudFlags: [],
        riskFlags: Array.isArray(result.riskFlags)
            ? result.riskFlags.map(String)
            : [],
        aiSummary: result.summary || '',
        classificationReasoning: result.classificationReasoning || undefined,
        keyFacts: Array.isArray(result.keyFacts)
            ? result.keyFacts.map(String)
            : [],
        intent: result.intent || undefined,
        priority: urgencyStatus,
        confidence,
        sentiment,
        requiresReply: Boolean(result.requiresReply),
        entities: Array.isArray(result.entities)
            ? result.entities.map(String)
            : [],
        attachmentInsights: normalizeAttachmentInsights(result.attachmentInsights),
        suggestedAction: result.suggestedAction || undefined,
        categorySync: Boolean(result.categorySync),
        validationStatus: 'pending',
    };
}
const CHUNK_SIZE = 12;
async function classifyChunk(graphToken, messages, force) {
    const apiBase = getClassifierApiUrl();
    const res = await fetch(`${apiBase}/api/classify`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${graphToken}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ messages, force }),
    });
    if (!res.ok) {
        const text = await res.text();
        let message = `Classifier API ${res.status}`;
        try {
            const json = JSON.parse(text);
            if (json.error)
                message = json.error;
        }
        catch {
            if (text)
                message = text.slice(0, 200);
        }
        throw new Error(message);
    }
    const json = (await res.json());
    return {
        classifications: json.classifications || [],
        errors: json.errors || [],
    };
}
export async function classifyMessages(graphToken, messages, options) {
    if (!messages.length) {
        return { classifications: [], errors: [] };
    }
    const force = Boolean(options?.force);
    const classifications = [];
    const errors = [];
    for (let i = 0; i < messages.length; i += CHUNK_SIZE) {
        const chunk = messages.slice(i, i + CHUNK_SIZE);
        const part = await classifyChunk(graphToken, chunk, force);
        classifications.push(...part.classifications);
        errors.push(...part.errors);
    }
    return { classifications, errors };
}
export function getClassifierApiBase() {
    return getClassifierApiUrl();
}
